// frontend/src/components/inventory/ProductFormModal.jsx
import React, { useState, useEffect } from "react";
import "../../components/common/Modal.css"; // Reuse modal styles
import "../../assets/styles/common.css"; // Assuming common styles might include grid/flex utilities

const ProductFormModal = ({
  isOpen,
  onClose,
  onSubmit, // onSubmit is only relevant for add/edit mode
  initialData, // This will be the product data for both edit and view
  distributors,
  isViewMode, // NEW PROP: boolean to indicate view mode
  onAddStockDetails, // NEW PROP: Function to call when "Add Stock Details" button is clicked
  onDeleteStockDetail, // NEW PROP: Function to call when "Delete Stock Detail" button is clicked
}) => {
  const [formData, setFormData] = useState({
    manufacturer: "",
    distributorId: "",
    genericName: "",
    brandName: "",
    category: "Medicine", // Default category
    type: "Tablet", // Default type
    content: "",
    price: "",
    // NEW FIELD: Add optimalStockAmount to the form state
    optimalStockAmount: "",
    // Ensure 'id' is part of the initial state, even if null for new products
    id: null,
  });
  const [errors, setErrors] = useState({});
  // NEW: State for local confirmation modal
  const [showStockDeleteConfirm, setShowStockDeleteConfirm] = useState(false);
  const [stockDetailToDelete, setStockDetailToDelete] = useState(null);

  // Determine if the modal is in "add new product" mode
  const isAddMode = !initialData && !isViewMode;

  useEffect(() => {
    if (isOpen) {
      // Set initial data for editing or viewing, or clear for adding
      setFormData(
        initialData
          ? {
              id: initialData.id || null, // <--- FIX: Include the product ID
              manufacturer: initialData.manufacturer || "",
              distributorId: initialData.distributorId || "",
              genericName: initialData.genericName || "",
              brandName: initialData.brandName || "",
              category: initialData.category || "Medicine",
              type: initialData.type || "Tablet",
              content: initialData.content || "",
              price: initialData.price || "",
              // NEW FIELD: Initialize optimalStockAmount from initialData
              optimalStockAmount: initialData.optimalStockAmount || "",
            }
          : {
              id: null, // <--- FIX: Ensure ID is null for new products
              manufacturer: "",
              distributorId: "",
              genericName: "",
              brandName: "",
              category: "Medicine",
              type: "Tablet",
              content: "",
              price: "",
              // NEW FIELD: Ensure optimalStockAmount is clear for new products
              optimalStockAmount: "",
            }
      );
      setErrors({}); // Clear errors when modal opens
    }
  }, [isOpen, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for the field being typed into
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.manufacturer)
      newErrors.manufacturer = "Manufacturer is required.";
    if (!formData.distributorId)
      newErrors.distributorId = "Distributor is required.";
    if (!formData.genericName)
      newErrors.genericName = "Generic Name is required.";
    if (!formData.brandName) newErrors.brandName = "Brand Name is required.";
    if (!formData.category) newErrors.category = "Category is required.";
    if (!formData.type) newErrors.type = "Type is required.";
    if (!formData.content) newErrors.content = "Content is required.";
    if (
      formData.price === "" ||
      isNaN(formData.price) ||
      parseFloat(formData.price) <= 0
    ) {
      newErrors.price = "Valid price is required.";
    }
    // NEW VALIDATION: Check if optimalStockAmount is a non-negative number
    if (
      formData.optimalStockAmount !== "" &&
      (isNaN(formData.optimalStockAmount) ||
        parseFloat(formData.optimalStockAmount) < 0)
    ) {
      newErrors.optimalStockAmount =
        "Optimal Stock Amount must be a positive number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isViewMode && validateForm()) {
      try {
        // Pass formData directly, which now includes 'id'
        await onSubmit({
          ...formData,
          distributorId: parseInt(formData.distributorId), // Ensure distributorId is an integer
          price: parseFloat(formData.price),
          // NEW: Ensure optimalStockAmount is a number
          optimalStockAmount:
            formData.optimalStockAmount !== ""
              ? parseInt(formData.optimalStockAmount)
              : null,
        });
        onClose(); // Close modal only after successful submission
      } catch (error) {
        console.error("Error saving product:", error);
        setErrors((prev) => ({
          ...prev,
          form: "Failed to save product. Please try again.",
        }));
      }
    }
  };

  // NEW: Handler to show confirmation for stock detail deletion
  const handleConfirmDeleteStockDetail = (detail) => {
    setStockDetailToDelete(detail);
    setShowStockDeleteConfirm(true);
  };

  // NEW: Handler for confirming stock detail deletion
  const handleProceedDeleteStockDetail = () => {
    if (stockDetailToDelete && onDeleteStockDetail) {
      onDeleteStockDetail(initialData.id, stockDetailToDelete.id);
    }
    setShowStockDeleteConfirm(false);
    setStockDetailToDelete(null);
  };

  // NEW: Handler for canceling stock detail deletion
  const handleCancelDeleteStockDetail = () => {
    setShowStockDeleteConfirm(false);
    setStockDetailToDelete(null);
  };

  if (!isOpen) return null;

  const categories = ["Medicine", "Medical Supplies", "Others"];
  const types = [
    "Tablet",
    "Capsule",
    "Caplet",
    "Syrup",
    "Drops",
    "Oral",
    "Oral Suspension",
    "Vial",
    "Cream",
    "Ointment",
    "Gel",
    "Spray",
    "Liquid Gels",
    "Suppositories",
    "Patches",
    "Liniment",
    "Injections",
    "Sheets",
  ];

  // Helper to find distributor name for display in view mode
  const getDistributorName = (id) => {
    const dist = distributors.find((d) => d.id === id);
    return dist ? dist.companyName : "N/A";
  };

  // Helper to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(); // Or toLocaleString() for time as well
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            {isViewMode
              ? "Product Details"
              : initialData
              ? "Edit Product"
              : "Add New Product"}
          </h2>
          <button className="modal-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-columns">
            {/* Distributor (Supplier) */}
            <div className="form-group">
              <label htmlFor="distributorId">Distributor (Supplier):</label>
              {isViewMode ? (
                <p className="view-value">
                  {getDistributorName(initialData?.distributorId)}
                </p>
              ) : (
                <select
                  id="distributorId"
                  name="distributorId"
                  value={formData.distributorId}
                  onChange={handleChange}
                  className={errors.distributorId ? "input-error" : ""}
                >
                  <option value="">Select a distributor</option>
                  {distributors.map((dist) => (
                    <option key={dist.id} value={dist.id}>
                      {dist.companyName}
                    </option>
                  ))}
                </select>
              )}
              {errors.distributorId && !isViewMode && (
                <span className="error-message">{errors.distributorId}</span>
              )}
            </div>

            {/* Category */}
            <div className="form-group">
              <label htmlFor="category">Category:</label>
              {isViewMode ? (
                <p className="view-value">{initialData?.category}</p>
              ) : (
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={errors.category ? "input-error" : ""}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
              {errors.category && !isViewMode && (
                <span className="error-message">{errors.category}</span>
              )}
            </div>

            {/* Manufacturer */}
            <div className="form-group">
              <label htmlFor="manufacturer">Manufacturer:</label>
              {isViewMode ? (
                <p className="view-value">{initialData?.manufacturer}</p>
              ) : (
                <input
                  type="text"
                  id="manufacturer"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                  className={errors.manufacturer ? "input-error" : ""}
                />
              )}
              {errors.manufacturer && !isViewMode && (
                <span className="error-message">{errors.manufacturer}</span>
              )}
            </div>

            {/* Type */}
            <div className="form-group">
              <label htmlFor="type">Type:</label>
              {isViewMode ? (
                <p className="view-value">{initialData?.type}</p>
              ) : (
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className={errors.type ? "input-error" : ""}
                >
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              )}
              {errors.type && !isViewMode && (
                <span className="error-message">{errors.type}</span>
              )}
            </div>

            {/* Generic Name */}
            <div className="form-group">
              <label htmlFor="genericName">Generic Name:</label>
              {isViewMode ? (
                <p className="view-value">{initialData?.genericName}</p>
              ) : (
                <input
                  type="text"
                  id="genericName"
                  name="genericName"
                  value={formData.genericName}
                  onChange={handleChange}
                  className={errors.genericName ? "input-error" : ""}
                />
              )}
              {errors.genericName && !isViewMode && (
                <span className="error-message">{errors.genericName}</span>
              )}
            </div>

            {/* Content */}
            <div className="form-group">
              <label htmlFor="content">
                Content (e.g., 500mg, 10ml, 100 pcs):
              </label>
              {isViewMode ? (
                <p className="view-value">{initialData?.content}</p>
              ) : (
                <input
                  type="text"
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  className={errors.content ? "input-error" : ""}
                />
              )}
              {errors.content && !isViewMode && (
                <span className="error-message">{errors.content}</span>
              )}
            </div>

            {/* Brand Name */}
            <div className="form-group">
              <label htmlFor="brandName">Brand Name:</label>
              {isViewMode ? (
                <p className="view-value">{initialData?.brandName}</p>
              ) : (
                <input
                  type="text"
                  id="brandName"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleChange}
                  className={errors.brandName ? "input-error" : ""}
                />
              )}
              {errors.brandName && !isViewMode && (
                <span className="error-message">{errors.brandName}</span>
              )}
            </div>

            {/* Total Stocks (Unit) - Conditionally rendered, now display only */}
            {!isAddMode && (
              <div className="form-group">
                <label htmlFor="totalStocks">Total Stocks (Unit):</label>
                <p className="view-value">{initialData?.totalStocks}</p>
              </div>
            )}

            {/* NEW: Optimal Stock Amount Field */}
            <div className="form-group">
              <label htmlFor="optimalStockAmount">Optimal Stock Amount:</label>
              {isViewMode ? (
                <p className="view-value">
                  {initialData?.optimalStockAmount || "N/A"}
                </p>
              ) : (
                <input
                  type="number"
                  id="optimalStockAmount"
                  name="optimalStockAmount"
                  value={formData.optimalStockAmount}
                  onChange={handleChange}
                  className={errors.optimalStockAmount ? "input-error" : ""}
                />
              )}
              {errors.optimalStockAmount && !isViewMode && (
                <span className="error-message">
                  {errors.optimalStockAmount}
                </span>
              )}
            </div>

            {/* Selling Price */}
            <div className="form-group">
              <label htmlFor="price">Selling Price:</label>
              {isViewMode ? (
                <p className="view-value">
                  ₱
                  {initialData?.price
                    ? parseFloat(initialData.price).toFixed(2)
                    : "0.00"}
                </p>
              ) : (
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="0.01" // Allow decimal prices
                  className={errors.price ? "input-error" : ""}
                />
              )}
              {errors.price && !isViewMode && (
                <span className="error-message">{errors.price}</span>
              )}
            </div>
          </div>

          {/* Table section for additional details in view mode */}
          {isViewMode && ( // This block now always renders in view mode
            <div className="product-details-table-section">
              <h3>Stock Details</h3>
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Batch Number</th>
                    <th>Stock Number</th>
                    <th>Date Added</th>
                    <th>Manufacturing Date</th>
                    <th>Expiration Date</th>
                    <th>Cost</th>
                    <th /> {/* NEW: Actions header */}
                  </tr>
                </thead>
                <tbody>
                  {initialData?.stockDetails &&
                  initialData.stockDetails.length > 0 ? (
                    initialData.stockDetails.map((detail) => (
                      <tr key={detail.id}>
                        <td>{detail.batchNumber || "N/A"}</td>
                        <td>{detail.stockNumber || "N/A"}</td>
                        <td>
                          {detail.createdAt
                            ? new Date(detail.createdAt).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>{formatDate(detail.manufacturingDate)}</td>
                        <td>{formatDate(detail.expirationDate)}</td>
                        <td>
                          ₱
                          {detail.cost
                            ? parseFloat(detail.cost).toFixed(2)
                            : "0.00"}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() =>
                              handleConfirmDeleteStockDetail(detail)
                            } // Show confirmation
                            className="button button-danger button-icon-only button-small"
                            title="Delete Stock Detail"
                          >
                            &#x1F5D1; {/* Trash can icon */}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        {" "}
                        {/* Updated colspan */}
                        No stock details available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <br />
              {/* Button to add Stock details - always visible in view mode */}
              <div className="modal-additional-actions">
                <button
                  type="button"
                  onClick={() => onAddStockDetails(initialData)} // Pass the current product data
                  className="button button-primary"
                >
                  Add Stock Details
                </button>
              </div>
            </div>
          )}

          <div className="modal-actions">
            {!isViewMode && ( // Only show submit button in add/edit mode
              <button type="submit" className="button button-primary">
                {initialData ? "Save Changes" : "Add Product"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="button button-secondary"
            >
              {isViewMode ? "Close" : "Cancel"}
            </button>
          </div>
        </form>
      </div>

      {/* NEW: Stock Detail Delete Confirmation Modal */}
      {showStockDeleteConfirm && stockDetailToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button
                className="modal-close-button"
                onClick={handleCancelDeleteStockDetail}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the stock detail with Stock
                Number: <strong>{stockDetailToDelete.stockNumber}</strong> and
                Batch Number:{" "}
                <strong>{stockDetailToDelete.batchNumber || "N/A"}</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="button button-danger"
                onClick={handleProceedDeleteStockDetail}
              >
                Yes, Delete
              </button>
              <button
                className="button button-secondary"
                onClick={handleCancelDeleteStockDetail}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductFormModal;
